import React, { useContext, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { sessionContext } from '@marketing-milk/frontend'
import { Permissions } from '@marketing-milk/interfaces'
import { usePacketStrategy, useSprintPacket } from 'hooks/api/useSprintPackets'
import { useSprint } from 'hooks/api/useSprint'
import { useAdCreativeReview } from 'hooks/api/useAdCreativeReview'
import { ReviewCreativeCard } from './review/ReviewCreativeCard'
import { ReviewCreativePreviewModal } from './review/ReviewCreativePreviewModal'
import { ReviewThemeSlide } from './review/ReviewThemeSlide'
import { adPosition, adStepKey, buildReviewSteps } from './review/reviewSteps'
import { useReviewKeyNav } from './review/useReviewKeyNav'
import { CreativeCardList } from './editor/sections/CreativeCardList'
import { joinStrategy } from './editor/sections/joinStrategy'
import { creativeWizardUrl } from '../creatives/creativeUrl'
import { usePacketFonts } from './usePacketFonts'
import { dark, type as typo } from './tokens'

// The one place ads get reviewed, for clients and employees alike: instead of
// paging through the deck, this lists every ad in the sprint so it can be
// approved or rejected directly (with a reason on rejection).
//
// An employee's verdict IS the client's — an approval reads Approved everywhere,
// and a rejection gates the ad out of the campaign picker exactly as the client's
// own would — so both cohorts hit the same endpoint with the same buttons. What
// employees additionally get is the ability to undo a recorded approval and to
// jump to the Creative Wizard.
export function ReviewSprintPacket() {
  usePacketFonts()
  const { businessID, packetID } = useParams<{ businessID: string; packetID: string }>()
  const businessIDNum = Number(businessID)

  // Builder access controls optional UI and broad cache invalidation; actor identity
  // controls the verdict flag the server requires. Keep them separate so an employee
  // with review-only access neither 400s nor receives builder-only affordances.
  const { user } = useContext(sessionContext)
  const hasBuilderAccess = !!user?.permissions.includes(Permissions.AccessAdBuilderApp)
  const asClientVerdict = !user || user.type !== 'Client'

  const { packet, isLoading: packetLoading } = useSprintPacket(Number(packetID))
  const { sprintCreatives, sprintCreativesLoading } = useSprint(packet?.sprintID)
  const { strategy, isLoading: strategyLoading } = usePacketStrategy(Number(packetID))

  // One mutation for the whole grid rather than one per card. ~50 cards and the
  // modal can all submit a verdict, and giving each its own is how the quick
  // Approve and the modal's Reject drift apart.
  const { submitVerdict, removeApproval } = useAdCreativeReview(businessIDNum, {
    asClientVerdict,
    hasBuilderAccess,
  })
  const submitting = submitVerdict.isLoading || removeApproval.isLoading

  const loading = packetLoading || sprintCreativesLoading || strategyLoading
  const creatives = sprintCreatives ?? []

  // The order the walkthrough runs in, matching what's on screen — each theme's
  // slide, then its ads, then anything ungrouped. The raw sprint list is a
  // different order entirely once grouping applies, so prev/next/advance walk
  // this and never the sprint's own order.
  const steps = useMemo(() => {
    return buildReviewSteps(joinStrategy(strategy, creatives))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, sprintCreatives])

  const introText =
    packet?.narrative?.find(block => block.sectionKey === 'creative-strategy')?.richText?.trim() ??
    ''

  // One atomic object rather than two separate `useState`s — `handleAdvance`
  // below fires after an `await` (a promise continuation, not a React event
  // handler), where React doesn't guarantee batching two separate `setState`
  // calls into a single render. With two states, that produced a real bug: a
  // render where the id had already moved to the next ad but the mode hadn't
  // caught up yet, mounting that ad's modal in reject mode by mistake — and
  // once mounted, its local `rejecting` state stuck there even once the mode
  // prop corrected itself a render later. One `setOpen` call can't split like
  // that.
  const [open, setOpen] = useState<{ stepKey: string; mode: 'view' | 'reject' } | null>(null)
  const openIndex = open ? steps.findIndex(step => step.key === open.stepKey) : -1
  const openStep = openIndex >= 0 ? steps[openIndex] : null

  const openAt = (creativeID: number, mode: 'view' | 'reject' = 'view') =>
    setOpen({ stepKey: adStepKey(creativeID), mode })

  // Successful approve/reject advances to the next step (slideshow-style) —
  // whatever comes next in the displayed order, regardless of which ad the client
  // started on — and only actually closes once there's nothing after it. Crossing
  // into a new theme lands on that theme's slide rather than straight onto its
  // first ad, which is the only marker that the reasoning changed.
  const handleAdvance = () => {
    const next = steps[openIndex + 1]
    setOpen(next ? { stepKey: next.key, mode: 'view' } : null)
  }

  // Manual back/forward through the deck, independent of approving/rejecting —
  // always lands in view mode.
  const goToPrev = () => {
    const prev = steps[openIndex - 1]
    if (prev) setOpen({ stepKey: prev.key, mode: 'view' })
  }
  const goToNext = () => {
    const next = steps[openIndex + 1]
    if (next) setOpen({ stepKey: next.key, mode: 'view' })
  }

  useReviewKeyNav({ active: !!openStep, onPrev: goToPrev, onNext: goToNext })

  return (
    <div style={{ minHeight: '100vh', background: dark.bg }}>
      {/* The eyebrow and the way back out are pinned. This page is dark and
        reads as a modal, so the instinct on arriving is to dismiss it rather
        than to scroll for an exit; the heading can scroll away, the label
        saying where you are and the link out cannot. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: dark.bg,
          borderBottom: `1px solid ${dark.hairline}`,
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
          }}
        >
          <div
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              fontWeight: typo.weight.semibold,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: dark.accent,
            }}
          >
            Client review
          </div>

          <Link
            to={`/${businessID}/sprint-packets/${packetID}/present#creative`}
            style={{
              flexShrink: 0,
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              fontWeight: typo.weight.semibold,
              color: dark.accent,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View full sprint packet →
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 32px 48px' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: typo.family.serif,
            fontWeight: typo.weight.regular,
            fontSize: typo.size.h1,
            color: dark.text,
          }}
        >
          Review ads
        </h1>
        <p
          style={{
            marginTop: 8,
            marginBottom: 0,
            fontFamily: typo.family.sans,
            fontSize: typo.size.bodyLg,
            color: dark.textMuted,
          }}
        >
          {packet ? packet.sprint?.name ?? `Sprint #${packet.sprintID}` : ''}
        </p>

        {/* The creative section's opening prose, same block the deck reads —
          this page is the client's other way into the same section, so it
          shouldn't arrive stripped of the reasoning. No empty-state placeholder:
          the deck's is a prompt to the AE. */}
        {introText && (
          <p
            style={{
              marginTop: 32,
              marginBottom: 0,
              fontFamily: typo.family.serif,
              fontSize: 24,
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              color: dark.text,
            }}
          >
            {introText}
          </p>
        )}

        <div style={{ marginBottom: 36 }} />

        {loading && (
          <div style={{ fontFamily: typo.family.sans, color: dark.textMuted }}>Loading…</div>
        )}

        {!loading && creatives.length === 0 && (
          <div style={{ fontFamily: typo.family.sans, color: dark.textMuted }}>
            There are no ads to review yet.
          </div>
        )}

        {/* The same grouped layout the packet's Creative section uses, with the
          review tile swapped in for the packet's. Loading and empty are handled
          above rather than by the list, whose own copy addresses an AE. */}
        {!loading && creatives.length > 0 && (
          <CreativeCardList
            palette={dark}
            creatives={creatives}
            strategy={strategy}
            renderCard={creative => (
              <ReviewCreativeCard
                creative={creative}
                onOpen={() => openAt(creative.id)}
                onOpenReject={() => openAt(creative.id, 'reject')}
                onApprove={() => submitVerdict.mutate({ creativeID: creative.id, approved: true })}
                submitting={submitting}
              />
            )}
          />
        )}
      </div>

      {openStep?.kind === 'theme' && (
        <ReviewThemeSlide
          key={openStep.key}
          label={openStep.group.label}
          blurb={openStep.group.blurb}
          adCount={openStep.adCount}
          hasPrev={openIndex > 0}
          hasNext={openIndex < steps.length - 1}
          onPrev={goToPrev}
          onNext={goToNext}
          onClose={() => setOpen(null)}
        />
      )}

      {openStep?.kind === 'ad' && (
        <ReviewCreativePreviewModal
          key={openStep.key}
          creative={openStep.creative}
          startInReject={open?.mode === 'reject'}
          position={adPosition(steps, openIndex)}
          themeLabel={openStep.themeLabel}
          hasPrev={openIndex > 0}
          hasNext={openIndex < steps.length - 1}
          onPrev={goToPrev}
          onNext={goToNext}
          onSubmitVerdict={verdict =>
            submitVerdict.mutateAsync({ creativeID: openStep.creative.id, ...verdict })
          }
          submitting={submitting}
          onRemoveApproval={
            hasBuilderAccess ? () => removeApproval.mutate(openStep.creative.id) : undefined
          }
          onOpenSource={
            hasBuilderAccess
              ? () =>
                  window.open(
                    creativeWizardUrl(businessIDNum, openStep.creative.id),
                    '_blank',
                    'noopener'
                  )
              : undefined
          }
          onAdvance={handleAdvance}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}
